<script lang="ts" context="module">
  declare global {
    // noinspection JSUnusedGlobalSymbols
    interface Window {
      editors: Record<string, {
        value: string,
        attachments: CommentAttachment[],
        uploadsCount: number
      }>
    }
  }
</script>
<script lang="ts">
  import Loading from '../../Loading_svelte.svelte'
  import type { CommentAttachment } from "../../comments/CommentTypes";
  import { onMount } from "svelte";
  import { sgAlert } from "../../../plugins/SgAlert";
  import { showToast } from "../../toast";
  import { captureFetchException } from "../../../common/helpers";
  import Api from "../../../api";
  import AuthDialog from "../../Auth/AuthDialog";
  import css from '../../../../css/comments.pcss.json'
  import CommentsTextarea from "./CommentsTextarea.svelte";
  import CommentAttachmentsEditable from "../attachments/CommentAttachmentsEditable.svelte";

  export let uid: string
  export let value: string = ''
  export let sending: boolean = false
  export let attachments: CommentAttachment[] = []
  export let inputName: string = 'comment_text'
  export let inputClassName: string = css['editor-textareas']
  export let placeholder: string = 'Текст комментария'
  // noinspection IncorrectFormatting
  export let showAuth: () => void = () => {}

  const attachmentsLimit = 6
  const attachmentsLimitReached = `Максимальное количество вложений — ${attachmentsLimit} шт.`
  const instagramRegex = /^https?:\/\/(www\.)?(instagram|facebook)\.com/
  let uploadsCount: number = 0
  let attachmentsCount = 0
  let uniqueAttachmentNumber = 0
  let stateAttachments: CommentAttachment[] = []

  let editorRef: CommentsTextarea

  onMount(() => {
    if (!window.editors) {
      window.editors = {}
    }
    if (window.editors[uid]) {
      const editorState = window.editors[uid]
      value = editorState.value
      attachments = editorState.attachments
      uploadsCount = editorState.uploadsCount
    }
  })

  if (attachments) {
    stateAttachments = attachments.map(attachment => {
      return {
        ...attachment,
        UUID: ++uniqueAttachmentNumber
      }
    })
  }

  $: {
    if (!window.editors) {
      window.editors = {}
    }
    window.editors[uid] = {
      value,
      attachments,
      uploadsCount
    }
  }

  const saveValue = (value: string) => {
    window.editors[uid].value = value
  }

  export const focus = () => {
    editorRef?.focus()
  }

  const addAttachment = (attachment: CommentAttachment) => {
    if (attachmentsCount >= attachmentsLimit) {
      sgAlert(attachmentsLimitReached)
      return false
    }

    attachmentsCount += 1
    attachment.UUID = ++uniqueAttachmentNumber
    stateAttachments = [...stateAttachments, attachment]
    attachments = stateAttachments
    window.editors[uid].attachments = stateAttachments
    return true
  }

  const attachGame = (gameId: number, gameInfo: {
    poster: string,
    status: number,
    url: string,
    name: string,
    releaseDate: string
  }) => {
    const exists = stateAttachments.find(attachment => attachment.type === 'game' && attachment.gameId === gameId)

    if (exists) {
      sgAlert('Игра уже добавлена')
      return
    }

    const url = new URL(window.location.href)
    url.search = ''
    url.hash = ''
    url.pathname = gameInfo.url
    addAttachment({
      type: 'game',
      url: url.toString(),
      gameId,
      gameInfo
    })
  }

  const updateAttachment = (UUID: number, attachment: CommentAttachment) => {
    const index = stateAttachments.findIndex(attachment => attachment.UUID === UUID)
    stateAttachments[index] = {
      ...attachment,
      UUID
    }
    attachments = stateAttachments
    window.editors[uid].attachments = stateAttachments
  }

  const removeAttachment = (UUID: number) => {
    stateAttachments = stateAttachments.filter(attachment => attachment.UUID !== UUID)
    attachments = stateAttachments
    window.editors[uid].attachments = stateAttachments
    attachmentsCount = stateAttachments.length
  }

  const incrementUploads = () => {
    uploadsCount += 1
    window.editors[uid].uploadsCount = uploadsCount
  }

  const decrementUploads = () => {
    uploadsCount -= 1
    window.editors[uid].uploadsCount = uploadsCount
  }

  export const validate = async () => {
    if (await editorRef.hasLinks()) {
      return false
    }

    if (uploadsCount > 0) {
      sgAlert('Подожди, пока все вложения загрузятся')
      return false
    }

    if (editorRef.isEmpty() && attachments.length === 0) {
      sgAlert('Минимальный размер сообщения — 3 символа')
      return false
    }

    return true
  }

  const uploadImage = async (file: File) => {
    let index = attachmentsCount
    let UUID: number

    return await new Promise<void>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = event => {
        const dataUrl = event.target.result as string
        try {
          let result = addAttachment({
            type: 'image',
            url: dataUrl,
            preview_url: dataUrl,
            uploading: true
          })
          UUID = uniqueAttachmentNumber
          if (result) {
            resolve()
          } else {
            reject()
          }
        } catch (e) {
          showToast({
            type: 'warning',
            text: e.message
          })
          reject()
        }
      }
      reader.readAsDataURL(file)
    })
      .then(() => {
        incrementUploads()
        const formData = new FormData()
        formData.append('img_file', file)
        fetch('/ajax/upload.image.php', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
          .then(response => response.json())
          .then(result => {
            decrementUploads()
            if (result.status !== 0) {
              throw new Error(result.message)
            }

            const meta: CommentAttachment = {
              type: 'image',
              url: result.image,
              preview_url: result.preview,
            }

            if (typeof index !== 'undefined') {
              updateAttachment(UUID, meta)
            } else {
              addAttachment(meta)
            }
          })
          .catch(e => {
            captureFetchException(e)
            removeAttachment(UUID)
            decrementUploads()
            showToast({
              type: 'warning',
              text: e.message
            })
          })
      })
  }

  const addLink = async (url: string) => {
    if (instagramRegex.test(url)) {
      sgAlert('Ссылки на Instagram и Facebook запрещены')
      return
    }
    try {
      const allowed = addAttachment({
        type: 'link',
        url,
        uploading: true
      })

      if (!allowed) {
        return
      }

      const UUID = uniqueAttachmentNumber
      incrementUploads()
      return Api.post({ url: 'link-info', body: { url } })
        .then((result: CommentAttachment & { success: boolean }) => {
          if (typeof result.success !== 'undefined' && !result.success) {
            showToast({
              type: 'warning',
              text: 'Ошибка при получении информации о ссылке'
            })
            return
          }
          updateAttachment(UUID, result)
        })
        .catch((e) => {
          removeAttachment(UUID)
          if (e.message.startsWith('403')) {
            if (showAuth) {
              showAuth()
            } else {
              AuthDialog.show()
            }
          }
        })
        .finally(() => {
          decrementUploads()
        })
    } catch (e) {
      decrementUploads()
    }
  }

  export const reset = () => new Promise<void>(resolve => {
    value = ''
    stateAttachments = []
    attachments = []
    uploadsCount = 0
    window.editors[uid] = {
      value,
      attachments,
      uploadsCount
    }
    editorRef?.reset()
    resolve()
  })
</script>

<div class="comment-editor">
  <CommentsTextarea
    bind:this={editorRef}
    bind:value={value}
    uploadImage={uploadImage}
    addLink={addLink}
    inputName={inputName}
    className={inputClassName}
    placeholder={placeholder}
    saveValue={saveValue}
    attachGame={attachGame}
  />
  <CommentAttachmentsEditable
    attachments={stateAttachments}
    removeAttachment={removeAttachment}
  />
  {#if sending}
    <div class={css['comment-form__overlay']}>
      <Loading string="Отправляем комментарий…"/>
    </div>
  {/if}
</div>
